import type { ComponentPropsWithoutRef, CSSProperties, ElementType } from 'react';
import type { SpaceKey } from '../../tokens/space';
import { cx } from '../../utils/cx';
import { spaceVar } from '../../utils/space';
import s from './Stack.module.css';

type Align = 'start' | 'center' | 'end' | 'stretch' | 'baseline';
type Justify = 'start' | 'center' | 'end' | 'between';

type LayoutProps = ComponentPropsWithoutRef<'div'> & {
  as?: ElementType;
  /** Space token key: `gap={4}` → var(--gg-space-4). */
  gap?: SpaceKey;
  align?: Align;
  justify?: Justify;
};

const withGap = (gap: SpaceKey, style?: CSSProperties) =>
  ({ ...style, '--gap': spaceVar(gap) }) as CSSProperties;

/** Vertical rhythm. */
export function Stack({
  as: Tag = 'div',
  gap = 4,
  align,
  justify,
  className,
  style,
  ...rest
}: LayoutProps) {
  return (
    <Tag
      className={cx(s.stack, className)}
      data-align={align}
      data-justify={justify}
      style={withGap(gap, style)}
      {...rest}
    />
  );
}

/** Horizontal row that wraps by default. */
export function Inline({
  as: Tag = 'div',
  gap = 2,
  align = 'center',
  justify,
  wrap = true,
  className,
  style,
  ...rest
}: LayoutProps & { wrap?: boolean }) {
  return (
    <Tag
      className={cx(s.inline, className)}
      data-align={align}
      data-justify={justify}
      data-nowrap={wrap ? undefined : true}
      style={withGap(gap, style)}
      {...rest}
    />
  );
}
